import React, { useState, useCallback, useMemo, useEffect } from 'react';

type WalkthroughStep = {
  title: string;
  description: string;
  element?: string; // CSS selector for element to highlight
  position?: 'top' | 'bottom' | 'left' | 'right';
  action?: string; // Optional action description
};

type Walkthrough = {
  id: string;
  name: string;
  icon: string;
  steps: WalkthroughStep[];
};

const walkthroughs: Walkthrough[] = [
  {
    id: 'git',
    name: 'Git Version Control',
    icon: 'GIT',
    steps: [
      {
        title: 'Source Control Panel',
        description: 'Access Git features through the Source Control panel in the sidebar. Click the branch icon to see changed files, stage changes, and commit.',
        element: '.sidebar-section[data-section="git"]',
        position: 'right',
      },
      {
        title: 'Staging Changes',
        description: 'Click the + button next to a file to stage it for commit. You can also stage all changes at once.',
        action: 'Try staging a file by clicking the + button',
      },
      {
        title: 'Committing',
        description: 'Write a commit message and click "Commit" to save your changes. Good commit messages describe what changed and why.',
        action: 'Write a descriptive commit message',
      },
      {
        title: 'Branch Management',
        description: 'Use the Command Palette (Cmd+Shift+P) to create, switch, or delete branches. Search for "Git:" commands.',
        action: 'Try opening Command Palette and searching for "Git"',
      },
      {
        title: 'Git Blame',
        description: 'Enable Git blame from the Command Palette to see who made each change. Search for "Toggle Git blame".',
        action: 'Enable Git blame to see line-by-line history',
      },
    ],
  },
  {
    id: 'editor',
    name: 'Editor Features',
    icon: 'CODE',
    steps: [
      {
        title: 'Multi-Cursor Editing',
        description: 'Select a word and press Cmd+D to select the next occurrence. Press Cmd+Shift+L to select all occurrences at once.',
        action: 'Try selecting a word and pressing Cmd+D',
      },
      {
        title: 'Go to Definition',
        description: 'Press F12 or Cmd+Click on a function or variable to jump to its definition. Works across files!',
        action: 'Cmd+Click on an import to jump to that file',
      },
      {
        title: 'Find All References',
        description: 'Press Shift+F12 to find all usages of a symbol across your project.',
        action: 'Place cursor on a function name and press Shift+F12',
      },
      {
        title: 'Code Folding',
        description: 'Collapse code blocks with Cmd+Shift+[ or expand with Cmd+Shift+]. Fold by indent level with Cmd+K then Cmd+1/2/3.',
        action: 'Try folding a function with Cmd+Shift+[',
      },
      {
        title: 'Rename Symbol',
        description: 'Press F2 to rename a variable or function across all files. The rename will be applied everywhere it\'s used.',
        action: 'Place cursor on a variable and press F2',
      },
      {
        title: 'Quick Fix',
        description: 'Press Cmd+. when there\'s an error or warning to see quick fixes like auto-import or refactoring suggestions.',
        action: 'Try pressing Cmd+. on an import statement',
      },
    ],
  },
  {
    id: 'navigation',
    name: 'Code Navigation',
    icon: 'NAV',
    steps: [
      {
        title: 'Quick Open File',
        description: 'Press Cmd+P to quickly open any file by name. Just start typing the filename.',
        element: '.go-to-file-modal',
        action: 'Press Cmd+P and type a filename',
      },
      {
        title: 'Go to Symbol',
        description: 'Press Cmd+Shift+O to jump to any function, class, or symbol in the current file.',
        action: 'Press Cmd+Shift+O to see all symbols',
      },
      {
        title: 'Command Palette',
        description: 'Press Cmd+Shift+P to access all commands. Search for any action or setting.',
        action: 'Press Cmd+Shift+P and explore commands',
      },
      {
        title: 'Breadcrumb Navigation',
        description: 'Click on the breadcrumb path at the top of the editor to quickly navigate to parent folders or symbols.',
        element: '.breadcrumb-nav',
        position: 'bottom',
      },
      {
        title: 'Outline Panel',
        description: 'Use the Outline panel in the sidebar to see all symbols in the current file and click to navigate.',
        action: 'Look for the Outline section in the sidebar',
      },
    ],
  },
  {
    id: 'preview',
    name: 'Preview & Testing',
    icon: 'PLAY',
    steps: [
      {
        title: 'Live Preview',
        description: 'The preview panel shows your React app in real-time. Changes are reflected automatically with hot reload.',
        element: '.preview-panel',
        position: 'left',
      },
      {
        title: 'Device Emulation',
        description: 'Click the device dropdown to preview on different devices like iPhone, iPad, or custom sizes.',
        action: 'Try selecting a different device preset',
      },
      {
        title: 'Console Output',
        description: 'Console logs from your app appear in the Console tab below. Errors are highlighted in red.',
        element: '.log-panel',
        position: 'top',
      },
      {
        title: 'Network Inspector',
        description: 'Switch to the Network tab to see all fetch/XHR requests made by your app.',
        action: 'Click the Network tab to see requests',
      },
      {
        title: 'Screenshot & Recording',
        description: 'Use the camera icon to capture screenshots or the record button to create screen recordings.',
        action: 'Try taking a screenshot of your preview',
      },
    ],
  },
  {
    id: 'packages',
    name: 'Package Management',
    icon: 'PKG',
    steps: [
      {
        title: 'Open Package Manager',
        description: 'Use Command Palette (Cmd+Shift+P) and search for "Package Manager" to open the package management interface.',
        action: 'Open Command Palette and type "Package Manager"',
      },
      {
        title: 'Search Packages',
        description: 'Search for npm packages by name. You\'ll see package descriptions and version info.',
        action: 'Try searching for a package like "lodash"',
      },
      {
        title: 'Add Dependencies',
        description: 'Click "Add" for regular dependencies or "Dev" for devDependencies. The package.json is updated automatically.',
        action: 'Try adding a package to your project',
      },
      {
        title: 'Check for Updates',
        description: 'Switch to the Updates tab and click "Check for Updates" to find outdated packages.',
        action: 'Check if any of your packages need updates',
      },
      {
        title: 'View Dependencies',
        description: 'The Dependencies panel in the sidebar shows all installed packages. Click a package to open its npm page.',
        action: 'Look for the Dependencies section in the sidebar',
      },
    ],
  },
  {
    id: 'accessibility',
    name: 'Accessibility Features',
    icon: 'A11Y',
    steps: [
      {
        title: 'High Contrast Mode',
        description: 'Enable high contrast mode from Command Palette for better visibility. Search for "Toggle High Contrast".',
        action: 'Try enabling high contrast mode',
      },
      {
        title: 'Reduced Motion',
        description: 'If you prefer less animation, enable reduced motion mode. Respects your system preferences too.',
        action: 'Search for "Toggle Reduced Motion" in Command Palette',
      },
      {
        title: 'Color Blind Modes',
        description: 'Color blind simulation modes help you see how your app looks to users with different color vision.',
        action: 'Try different color blind modes from Command Palette',
      },
      {
        title: 'Font Size',
        description: 'Use Cmd+Plus to increase font size, Cmd+Minus to decrease, or Cmd+0 to reset.',
        action: 'Try adjusting the font size',
      },
      {
        title: 'Keyboard Navigation',
        description: 'All features are accessible via keyboard. Press Cmd+K Cmd+S to see all keyboard shortcuts.',
        action: 'Open the keyboard shortcuts reference',
      },
    ],
  },
];

type FeatureWalkthroughModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

export const FeatureWalkthroughModal = React.memo(function FeatureWalkthroughModal({
  isOpen,
  onClose,
}: FeatureWalkthroughModalProps) {
  const [selectedWalkthrough, setSelectedWalkthrough] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState(0);

  const activeWalkthrough = useMemo(() => {
    return walkthroughs.find(w => w.id === selectedWalkthrough) || null;
  }, [selectedWalkthrough]);

  const handleSelectWalkthrough = useCallback((id: string) => {
    setSelectedWalkthrough(id);
    setCurrentStep(0);
  }, []);

  const handleBack = useCallback(() => {
    setSelectedWalkthrough(null);
    setCurrentStep(0);
  }, []);

  const handlePrevStep = useCallback(() => {
    setCurrentStep(prev => Math.max(0, prev - 1));
  }, []);

  const handleNextStep = useCallback(() => {
    if (activeWalkthrough) {
      if (currentStep < activeWalkthrough.steps.length - 1) {
        setCurrentStep(prev => prev + 1);
      } else {
        handleBack();
      }
    }
  }, [activeWalkthrough, currentStep, handleBack]);

  const handleClose = useCallback(() => {
    setSelectedWalkthrough(null);
    setCurrentStep(0);
    onClose();
  }, [onClose]);

  // Highlight element if specified
  useEffect(() => {
    if (!activeWalkthrough || !isOpen) return;

    const step = activeWalkthrough.steps[currentStep];
    if (!step.element) return;

    const el = document.querySelector(step.element);
    if (el) {
      el.classList.add('walkthrough-highlight');
      return () => {
        el.classList.remove('walkthrough-highlight');
      };
    }
  }, [activeWalkthrough, currentStep, isOpen]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen || !activeWalkthrough) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === 'Enter') {
        e.preventDefault();
        handleNextStep();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        handlePrevStep();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        if (activeWalkthrough) {
          handleBack();
        } else {
          handleClose();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, activeWalkthrough, handleNextStep, handlePrevStep, handleBack, handleClose]);

  if (!isOpen) return null;

  return (
    <div className="modal-backdrop" onClick={handleClose}>
      <div
        className="modal"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '550px', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}
      >
        <div className="modal-header">
          <div className="modal-title">
            {activeWalkthrough ? (
              <button
                onClick={handleBack}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--muted)',
                  cursor: 'pointer',
                  padding: '0 8px 0 0',
                  fontSize: '14px',
                }}
              >
                &larr;
              </button>
            ) : null}
            {activeWalkthrough ? activeWalkthrough.name : 'Feature Walkthroughs'}
          </div>
          <button className="modal-close" onClick={handleClose}>
            x
          </button>
        </div>

        <div className="modal-body" style={{ padding: '16px', overflow: 'auto', flex: 1 }}>
          {!activeWalkthrough ? (
            <>
              <p style={{ color: 'var(--muted)', fontSize: '13px', marginBottom: '16px' }}>
                Choose a feature to learn more about it with a guided walkthrough.
              </p>
              <div style={{ display: 'grid', gap: '12px' }}>
                {walkthroughs.map((w) => (
                  <button
                    key={w.id}
                    onClick={() => handleSelectWalkthrough(w.id)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '14px 16px',
                      background: 'var(--bg-secondary)',
                      border: '1px solid var(--border)',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'border-color 0.2s',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = 'var(--accent)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = 'var(--border)';
                    }}
                  >
                    <span style={{
                      width: '40px',
                      height: '40px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: 'var(--accent)',
                      color: 'white',
                      borderRadius: '8px',
                      fontWeight: 'bold',
                      fontSize: '11px',
                    }}>
                      {w.icon}
                    </span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 'bold', color: 'var(--text)', fontSize: '14px' }}>
                        {w.name}
                      </div>
                      <div style={{ color: 'var(--muted)', fontSize: '12px', marginTop: '2px' }}>
                        {w.steps.length} steps
                      </div>
                    </div>
                    <span style={{ color: 'var(--muted)', fontSize: '18px' }}>&rarr;</span>
                  </button>
                ))}
              </div>
            </>
          ) : (
            <>
              {/* Progress bar */}
              <div style={{
                display: 'flex',
                gap: '4px',
                marginBottom: '20px',
              }}>
                {activeWalkthrough.steps.map((_, i) => (
                  <div
                    key={i}
                    style={{
                      flex: 1,
                      height: '4px',
                      borderRadius: '2px',
                      background: i <= currentStep ? 'var(--accent)' : 'var(--border)',
                      transition: 'background 0.3s',
                    }}
                  />
                ))}
              </div>

              {/* Step content */}
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <div style={{
                  width: '60px',
                  height: '60px',
                  margin: '0 auto 20px',
                  background: 'var(--accent)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '24px',
                  fontWeight: 'bold',
                  color: 'white',
                }}>
                  {currentStep + 1}
                </div>

                <h3 style={{ margin: '0 0 12px', color: 'var(--text)', fontSize: '18px' }}>
                  {activeWalkthrough.steps[currentStep].title}
                </h3>

                <p style={{ color: 'var(--muted)', fontSize: '14px', lineHeight: '1.6', margin: '0 0 16px' }}>
                  {activeWalkthrough.steps[currentStep].description}
                </p>

                {activeWalkthrough.steps[currentStep].action && (
                  <div style={{
                    padding: '12px 16px',
                    background: 'rgba(59, 130, 246, 0.1)',
                    border: '1px solid var(--accent)',
                    borderRadius: '8px',
                    color: 'var(--accent)',
                    fontSize: '13px',
                  }}>
                    <strong>Try it:</strong> {activeWalkthrough.steps[currentStep].action}
                  </div>
                )}
              </div>

              {/* Navigation buttons */}
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '20px' }}>
                <button
                  className="pill-btn"
                  onClick={handlePrevStep}
                  disabled={currentStep === 0}
                  style={{ opacity: currentStep === 0 ? 0.5 : 1 }}
                >
                  Previous
                </button>
                <span style={{ color: 'var(--muted)', fontSize: '12px', alignSelf: 'center' }}>
                  {currentStep + 1} / {activeWalkthrough.steps.length}
                </span>
                <button
                  className="pill-btn"
                  onClick={handleNextStep}
                  style={{ background: 'var(--accent)', color: 'white' }}
                >
                  {currentStep === activeWalkthrough.steps.length - 1 ? 'Finish' : 'Next'}
                </button>
              </div>
            </>
          )}
        </div>

        {!activeWalkthrough && (
          <div className="modal-footer">
            <button className="pill-btn" onClick={handleClose}>
              Close
            </button>
          </div>
        )}
      </div>

      <style>{`
        .walkthrough-highlight {
          outline: 3px solid var(--accent) !important;
          outline-offset: 2px;
          box-shadow: 0 0 20px rgba(59, 130, 246, 0.3);
          z-index: 9999;
          position: relative;
        }
      `}</style>
    </div>
  );
});

export default FeatureWalkthroughModal;
